import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { changeAgentMode, createAgent, listAgents, setAgentStatus, updateAgent } from './api'
import type { AgentPersona } from './api'
import type { AgentModeChange, AgentModeValue, AgentSummary } from './types'

export function agentsQueryKey(tenantId: string) {
  return ['agents', 'list', tenantId] as const
}

/** Agentes do tenant, para a tela de gestao (lista, modo atual, status). */
export function useAgents(tenantId: string | undefined) {
  return useQuery({
    queryKey: agentsQueryKey(tenantId ?? ''),
    queryFn: () => listAgents(tenantId ?? ''),
    enabled: tenantId !== undefined,
  })
}

export interface ChangeAgentModeVariables {
  readonly agentId: string
  readonly mode: AgentModeValue
  /** Confirmacao explicita exigida SOMENTE para habilitar AutoReply (§24); ignorada nos demais modos. */
  readonly confirmAutoReply: boolean
}

/**
 * Muda o modo de um agente e reflete o novo modo na lista imediatamente. O modo atual precisa
 * aparecer certo assim que a troca conclui (o modo fica sempre visivel — docs/design/01 s11).
 */
export function useChangeAgentMode(tenantId: string) {
  const queryClient = useQueryClient()

  return useMutation<AgentModeChange, unknown, ChangeAgentModeVariables>({
    mutationFn: (variables) =>
      changeAgentMode(tenantId, variables.agentId, variables.mode, variables.confirmAutoReply),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentsQueryKey(tenantId) })
    },
  })
}

export function useCreateAgent(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation<AgentSummary, unknown, AgentPersona>({
    mutationFn: (persona) => createAgent(tenantId, persona),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentsQueryKey(tenantId) })
    },
  })
}

export function useUpdateAgent(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation<AgentSummary, unknown, { agentId: string; persona: AgentPersona }>({
    mutationFn: (variables) => updateAgent(tenantId, variables.agentId, variables.persona),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentsQueryKey(tenantId) })
    },
  })
}

export function useSetAgentStatus(tenantId: string) {
  const queryClient = useQueryClient()
  return useMutation<AgentSummary, unknown, { agentId: string; enabled: boolean }>({
    mutationFn: (variables) => setAgentStatus(tenantId, variables.agentId, variables.enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: agentsQueryKey(tenantId) })
    },
  })
}
